import {filter, transpose, p, s, lazy} from './lang.js';
import Notes from 'midi/Notes.js';
import {r_trig} from './trig.js';
import {nth} from './list.js';
import {chord} from './harmonize.js';
import {mod} from 'utils/mod.js';
import {t_transform_leaf} from './tlist.js';
import {
    FORWARD,
    REWIND,
    PLAY,
    RESTART,
    PAUSE} from 'effects/midi/jaki/ScaleRules.js';

window.scaleLazies=0;
window.scaleNoLazies=0;

export const scl = (p, a=1, b=2) => {
    let lazyValue;
    let lastPattern;
    return (...args) => {
        // given a pattern of dots and dashes return a list
        // of notes
        let _a = lazy(a);
        let _b = lazy(b);
        
        let _p = lazy(p, ...args);

        if (_p === lastPattern) {
            return lazyValue;
        }
        lastPattern = _p;
        let flattened = _p.flat(16);
        
        let note = 0;
        let notes = [note];
        
        for (let leaf of flattened) {
            if (notes.length >= 7) {
                break;
            }
            if (leaf.type) {
                let {
                    type,
                    value
                } = leaf.type;
                if (type === "-" && value === 0) {
                    note += _b;
                } else if (type === ".") {
                    note += _a;
                } else {
                    continue;
                }
                notes.push(note);
            }
        }
        lazyValue = notes;
        return lazyValue;
    } 
};

export const t_put_scale = (p, scale, scaleRules) => {
    let lazyValue;
    let lastScale;
    let lastRules;
    let lastPattern;
    return (...args) => {
        let _p = lazy(p, ...args);
        let _scale  = lazy(scale, ...args);
        let _rules = lazy(scaleRules, ...args);

        if (_p === lastPattern && _scale === lastScale && lastRules === _rules) {
            window.scaleLazies++;
            return lazyValue;
        } 
        window.scaleNoLazies++;

        lastRules = _rules;
        lastScale = _scale;
        lastPattern = _p;

        let l = _p.flat(16).length;
        let cursor = 0;
        for (let i=0; i < l; i++) {
            _p = t_transform_leaf(
                _p,
                i,
                (x) => {
                    x = {...x};
                    if (x.transpose !== null) {
                        if (_rules && _rules[i]) {
                            let {type, value}= _rules[i];
                            switch (type) {
                            case REWIND:
                                cursor = mod(cursor-value, _scale.length);
                                break;
                            case FORWARD:
                                cursor = mod(cursor+value, _scale.length);
                                break;
                            case RESTART:
                                cursor = 0;
                                break;
                            }
                        }
                        
                        x.transpose = _scale[cursor % _scale.length];

                        if (!_rules || !_rules[i] || _rules[i].type !== PAUSE) {
                            cursor++;
                        }
                    }
                    return x;
                },
                true)(...args);


        }
        lazyValue = _p;
        return lazyValue;
    };
};

export const degree_chord = (sc, degrees, octaves=[]) => {
    return (...args) => {
        let _sc = lazy(sc, ...args);
        let _degrees = lazy(degrees, ...args);
        let _octaves = lazy(octaves, ...args);
        let _chord = [];
        let i=0;
        let lastNote = undefined;
        for (let degree of _degrees) {
            degree = lazy(degree, ...args);
            degree--;
            let oct = _octaves[i%_octaves.length];
            if (oct !== undefined) {
                oct *= 12;
            } else {
                oct = 0;
            }
            if (degree > _sc.length ||
                (lastNote !== undefined && lastNote > _sc[degree] )) {
                oct +=  12;
                degree = degree % _sc.length;
            } 
            let note = _sc[degree] + oct;
            lastNote = note;
            _chord.push(note);
            i++;
        }
        return _chord;
        
    }; 
};

export const invert_chord = (
  notes, number=1, octave=0) => {
  return (...args) => {
    let _notes = [... lazy(notes, ...args)];
    
    let a = [];
    for (let i=0; i < number; i++) {
      a.push(_notes.shift()+12);
    }
    return [... _notes.map(n => n + 12*octave), ... a];
  };
};

// used to get the 13th degree of a scale, for example.
// important for building additional counter points over chords
export const scale_degree = (sc, degree=0) => {
  return (...args) => {
    let _sc = lazy(sc, ...args);
    let y = lazy(degree, ...args);
    
    let oct = 0;
      if (y >= _sc.length) {
      oct = 12;
      y = y % _sc.length;
    }
    let z= _sc[y] + oct;
    return z;
  };
};

export const chord_express = (
    myChord, 
    pattern,
    sc, 
    degrees) => {
        
        let i=-1;
        let lastStepNumber = undefined;
        let count = (stepData, stepNumber) => {
            // called before each note in the generated melody
            if (stepData && stepNumber !== lastStepNumber) {
                lastStepNumber = stepNumber;
                return i++;
            }
            lastStepNumber = stepNumber;
            return i;
        };
        
        return (...args) => {    
            let _chord = lazy(myChord, ...args);
            let _scale = lazy(sc, ...args);
            let _degrees = lazy(degrees, ...args);
            let [stepData, stepNumber, time, effect] = args;

            let expressions = _degrees.map(
                degree => scale_degree(
                    _scale, degree)(...args) );
            
            return p(
                s(
                    pattern,
                    transpose(nth(count, expressions)),
                    (stepData) => {
                        return filter(true);
                    },
                ),
                
                chord(_chord)(...args)); 
            
        };
    };

export const v_chord = (sc, voices) => {
    return (...args) => {
        let _sc = lazy(sc, ...args);
        let _voices = lazy(voices, ...args);
        let _chord = [];
        let i=0;
        let lastNote = undefined;
        for (let v of _voices) {
            let {voice, octave} = lazy(v, ...args);
            let degree = voice - 1;
            let oct = octave;
            if (oct !== undefined) {
                oct *= 12;
            } else {
                oct = 0;
            }
            if (degree >= _sc.length) {
                oct +=  12;
            } 
            let note = _sc[degree%_sc.length] + oct;
            lastNote = note;
            _chord.push(note);
            i++;
        }
        return _chord;
        
    }; 
};

export const gen_chord = (sc, voices, key=0) => {
    return (...args) => {
        let stepData = args[0];
        let _sc = lazy(sc, ...args);
        let _key = lazy(key, ...args);
        let _voices = lazy(voices, ...args);
        let _chord = [];
        let i=0;
        let lastNote = undefined;
        for (let v of _voices) {
            let {thick, flat, time, voice, octave} = lazy(v, ...args);
            let degree = voice - 1;
            let oct = octave;
            if (oct !== undefined) {
                oct *= 12;
            } else {
                oct = 0;
            }
            if (degree >= _sc.length) {
                oct +=  12;
            } 
            let note = _sc[degree % _sc.length] + oct + _key;
            if (flat) {
                note -= flat;
            }
            lastNote = note;
            
            _chord.push({note, time});
            if (thick !== undefined) {
                for (let i=1; i <= thick; i++ ){
                    _chord.push({note: note - i*12, time});
                }
            }
            i++;
        }

        return p(
            ...
                _chord.map(
                    ({note, time}) =>
                        r_trig(stepData, note, stepData.duration, time)));
    }; 
};


